/**
 * Read-only split-view document canvas shell. Must be rendered inside
 * {@link SplitCanvasProvider}.
 */
import { type ReactNode, useEffect } from 'react';
import type { Page } from '@rossum/api-client/pages';
import { alpha, useTheme } from '@rossum/ui/material';
import { CanvasRoot, Scrollbars } from '../canvas';
import {
  type Band,
  DocumentContent,
  type LayoutBandHeights,
  type PageId,
  type PageLayoutData,
} from '../document-canvas';
import { useSplitCanvasStoreApi } from './SplitCanvasProvider';

export type SplitCanvasProps = {
  pages: Page[];
  renderPageOverlays?: (pageModel: Page, layout: PageLayoutData) => ReactNode;
  bands?: LayoutBandHeights;
  renderHeader?: (band: Band) => ReactNode;
  renderBeforePage?: (
    pageModel: Page,
    layout: PageLayoutData,
    band: Band
  ) => ReactNode;
  renderAfterPage?: (
    pageModel: Page,
    layout: PageLayoutData,
    band: Band
  ) => ReactNode;
  pageRotations?: Record<PageId, number>;
  deletedPageIds?: readonly PageId[];
};

const useSyncPageRotations = (
  pageRotations: Record<PageId, number> | undefined
) => {
  const storeApi = useSplitCanvasStoreApi();

  useEffect(() => {
    if (!pageRotations) return;

    Object.entries(pageRotations).forEach(([key, degrees]) => {
      const pageId = Number(key);
      const state = storeApi.getState();
      const currentRotation = state.rotation.selectors.getBaseRotation(pageId);

      if (currentRotation !== degrees) {
        state.rotation.actions.setRotation(pageId, degrees);
      }
    });
  }, [pageRotations, storeApi]);
};

const DeletedPageScrim = ({ layout }: { layout: PageLayoutData }) => {
  const theme = useTheme();

  return (
    <rect
      data-cy="split-canvas-deleted-page-scrim"
      x={0}
      y={0}
      width={layout.originalWidth}
      height={layout.originalHeight}
      fill={alpha(theme.palette.background.default, 0.55)}
    />
  );
};

export const SplitCanvas = ({
  pages,
  renderPageOverlays,
  bands,
  renderHeader,
  renderBeforePage,
  renderAfterPage,
  pageRotations,
  deletedPageIds,
}: SplitCanvasProps) => {
  useSyncPageRotations(pageRotations);

  if (pages.length === 0) {
    return null;
  }

  const deletedIds = new Set(deletedPageIds ?? []);

  const renderOverlays = (pageModel: Page, layout: PageLayoutData) => (
    <>
      {renderPageOverlays?.(pageModel, layout)}
      {deletedIds.has(pageModel.id) ? (
        <DeletedPageScrim layout={layout} />
      ) : null}
    </>
  );

  return (
    <CanvasRoot>
      <DocumentContent
        pages={pages}
        renderPageOverlays={renderOverlays}
        bands={bands}
        renderHeader={renderHeader}
        renderBeforePage={renderBeforePage}
        renderAfterPage={renderAfterPage}
      />
      <Scrollbars />
    </CanvasRoot>
  );
};
